import React, { useState } from "react";
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import srilankaImg from "../assets/trainIMG.jpg";
import locationImg from "../assets/sigiriya.jpg";
import ColomboImg from "../assets/elephant.jpg";
import { useEffect as ReactUseEffect } from "react";
const primary = "#1f8f4e";

interface TourPackage {
  [x: string]: any;
  title: string;
  duration: string;
  price: string;
  description: string;
  days: { title: string; content: string }[];
}

export default function Tours() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const packages: TourPackage[] = [
    {
      title: "Sri Lanka Dream Route",
      duration: "10 Giorni / 9 Notti",
      price: "€1.500",
      description:
        "Un viaggio completo tra cultura, natura, safari, spiagge e città storiche dello Sri Lanka.",
      image: srilankaImg,
      days: [
        {
          title: "Giorno 01 - Negombo",
          content:
            "Arrivo in aeroporto e trasferimento a Negombo. Visita alla Chiesa di Santa Maria, al Forte Olandese, al villaggio dei pescatori, giro in barca lungo il Canale Hamilton, tramonto e vita notturna.",
        },
        {
          title: "Giorno 02 - Anuradhapura",
          content:
            "Safari a Wilpattu, Ruwanwalisaya, Tempio di Isurumuniya, esperienza al tramonto a Mihintale.",
        },
        {
          title: "Giorno 03 - Sigiriya",
          content:
            "Salita alla Roccia di Sigiriya, Pidurangala Rock, Safari a Minneriya.",
        },
        {
          title: "Giorno 04 - Kandy",
          content:
            "Tempio delle Grotte di Dambulla, Villaggio delle Spezie, Giardino Botanico Reale, Tempio del Dente.",
        },
        {
          title: "Giorno 05 - Nuwara Eliya",
          content:
            "Cascata di Ramboda, visita a una piantagione di tè, Tempio Seetha Amman, tramonto al Lago Gregory.",
        },
        {
          title: "Giorno 06 - Yala",
          content:
            "Nine Arches Bridge, Little Adam’s Peak, Safari a Yala (avvistamento dei leopardi).",
        },
        {
          title: "Giorno 07 - Bentota",
          content:
            "Forte Olandese di Galle, giro in barca sul fiume Madu, esperienza con i pescatori su palafitta.",
        },
      ],
    },

    {
      title: "Sri Lanka Highlights",
      duration: "5 Giorni / 4 Notti",
      price: "€750",
      description:
        "Mix perfetto tra cultura, natura e mare per un'esperienza intensa ma breve.",
      image: ColomboImg,
      days: [
        {
          title: "Giorno 01 - Kandy",
          content:
            "Orfanotrofio degli Elefanti di Pinnawala, Tempio del Dente, Giardino Botanico, Spettacolo Culturale.",
        },
        {
          title: "Giorno 02 - Nuwara Eliya",
          content:
            "Viaggio panoramico in treno, Cascata di Ramboda, Piantagione di tè, Lago Gregory.",
        },
        {
          title: "Giorno 03 - Bentota",
          content:
            "Tenuta Lunuganga, Sport acquatici, Centro di protezione delle tartarughe, Giro in barca sul fiume Madu.",
        },
        {
          title: "Giorno 04 - Colombo",
          content:
            "Tempio Gangaramaya, Galle Face Green, Shopping e vita notturna.",
        },
      ],
    },

    {
      title: "Sri Lanka Express",
      duration: "3 Giorni / 2 Notti",
      price: "€290",
      description:
        "Tour rapido ideale per chi ha poco tempo ma vuole vedere il meglio.",
      image: locationImg,
      days: [
        {
          title: "Giorno 01 - Kandy",
          content:
            "Trasferimento dall’aeroporto, tour della città, Tempio della Sacra Reliquia del Dente.",
        },
        {
          title: "Giorno 02 - Colombo",
          content:
            "Giardino Botanico Reale, visita a una fabbrica di tè, tour della città di Colombo.",
        },
      ],
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: "#f5efe6",
        py: 12,
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          fontWeight="bold"
          textAlign="center"
          mb={10}
          sx={{ letterSpacing: 1 }}
        >
          I nostri pacchetti turistici
        </Typography>

        <Box
          sx={{
            display: "grid",
            gap: 5,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
            },
          }}
        >
          {packages.map((tour, index) => (
            <Card
              key={index}
              sx={{
                borderRadius: 5,
                overflow: "hidden",
                boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                transition: "all 0.4s ease",
                "&:hover": {
                  transform: "translateY(-10px)",
                  boxShadow: "0 30px 60px rgba(0,0,0,0.12)",
                },
              }}
            >
              {/* IMAGE */}
              <Box
                sx={{
                  height: 220,
                  backgroundImage: `url(${tour.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />

              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" fontWeight="bold">
                  {tour.title}
                </Typography>

                <Typography mt={1} color="text.secondary">
                  {tour.duration}
                </Typography>

                <Typography
                  mt={3}
                  variant="h4"
                  fontWeight="bold"
                  color={primary}
                >
                  {tour.price}
                </Typography>

                <Typography mt={2} fontSize={14} color="text.secondary">
                  {tour.description}
                </Typography>

                <Box mt={3}>
                  {tour.days.map((day, i) => (
                    <Accordion
                      key={i}
                      elevation={0}
                      sx={{
                        bgcolor: "transparent",
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight="600">{day.title}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography fontSize={14} color="text.secondary">
                          {day.content}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>

                <Button
                  href="https://wa.me/393755752929"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 4,
                    bgcolor: primary,
                    borderRadius: 20,
                    py: 1.5,
                    fontWeight: "bold",
                    letterSpacing: 1,
                    boxShadow: "0 10px 20px rgba(31,143,78,0.3)",
                    "&:hover": {
                      bgcolor: "#166d3b",
                      transform: "scale(1.03)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Prenota subito!!
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
function useEffect(callback: () => void, dependencies: any[]) {
  ReactUseEffect(callback, dependencies);
}
